import { navigate } from "@reach/router";
import { Button } from "components/RFQ/Button";
import { TabHeader } from "components/TabHeader";
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { ReactComponent as BackIcon } from "../../../../../assets/icons/flip-backward.svg";
import { ReactComponent as AddIcon } from "../../../../../assets/icons/close.svg";
import { Flex, Spinner, useDisclosure } from "@chakra-ui/react";
import { Icon } from "assets/icons/Icon";
import { ProjectDetail } from "./ProjectDetail";
import { Documentation } from "./Documentation";
import {
	getDocumentType,
	getRFQId,
	getRFQType,
	getUnitMeasures,
	saveDraftRFQ,
} from "actions/RFQ/rfqsAction";
import { ReturnableSchedules } from "./ReturnableSchedules";
import { Form, Formik } from "formik";
import Quote from "./Quote/Quote";
import { Suppliers } from "./Suppliers";
import { PreviewRFQ } from "../PreviewRFQ";
import {
	formValuesChecker,
	getTabLists,
	validationSchema,
	validStepChecker,
	isQuoteFormInvalid,
} from "./createRFQUtils";
import TextButton from "components/RFQ/Button/TextButton";
import SubmitRFQModal from "./SubmitRFQModal";
import { NotFound } from "routes/NotFound";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "utils/helpers";
import { RFQContext } from "context/RFQContext";
import useRefreshWarning from "hooks/useRefreshWarning";
import { TemplateDetail } from "./_components/TemplateDetail";
import { RFQTabNavigation } from "./_components/RFQTabNavigation";
import moment from "moment";
import { getTemplateById, saveRFXTemplate } from "actions/RFQ/templateAction";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

const BackButton = ({ isNew, company_id, isEdit, isTemplate }) => {
	return (
		<Button
			btntype={"icon"}
			onClick={() => {
				if (isTemplate && isEdit) {
					navigate(`/account/${company_id}/manage-templates`);
				} else {
					navigate("..");
				}
			}}
			className={isNew && "!pointer-events-none"}
		>
			{isNew ? (
				<AddIcon
					style={{
						width: "25px",
						height: "25px",
						marginRight: "0px",
						rotate: "45deg",
					}}
				/>
			) : (
				<BackIcon
					style={{ width: "25px", height: "25px", marginRight: "0px" }}
				/>
			)}
		</Button>
	);
};

const CreateRFQPage = ({
	rfqId,
	isEdit,
	isTemplate,
	templateId: urlTemplateId,
}) => {
	const [selectedTab, setSelectedTab] = useState(1);
	const [rfqData, setRFQData] = useState(null);
	// eslint-disable-next-line
	const [templateId, setTemplateId] = useState(urlTemplateId || null);
	const tabButtonList = getTabLists(isTemplate);
	const [loadRFQData, setLoadRFQData] = useState(false);
	const formikRef = useRef();
	const [isSubmittingForm, setIsSubmittingForm] = useState(false);
	const [isValidForm, setIsValidForm] = useState(true);
	const [rfqTypes, setRFQTypes] = useState([]);
	const [documentCategories, setDocumentCategories] = useState([]);
	const [unitMeasures, setUnitMeasures] = useState([]);
	const [isNotFound, setIsNotFound] = useState(false);
	const [completedSteps, setCompletedSteps] = useState([]);
	const { company_id } = useSelector((state) => state?.auth?.user);
	const company = useSelector((state) => state?.auth?.current_company);
	const [validated, setValidated] = useState(false);
	const [validHandler, setValidHandler] = useState(null);
	const [touchedTabs, setTouchedTabs] = useState([]);
	const [isValidating, setIsValidating] = useState(false);
	const [isTabChanging, setIsTabChanging] = useState(false);
	const dispatch = useDispatch();

	const { onOpen, onClose, isOpen } = useDisclosure();

	const activeTab = tabButtonList.find((tab) => tab.value === selectedTab);
	const lastStep = isTemplate ? selectedTab === 4 : selectedTab === 5;
	const [disableLastStep, setDisableLastStep] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const enableOnChangeFormik = selectedTab !== 3;

	const { setRFQId, uploadInProgresses } = useContext(RFQContext);
	const isFileUploading = uploadInProgresses?.rfxProcurer?.length > 0;

	useRefreshWarning(isFileUploading);

	const tabRef = useRef(null);

	const [staticOptionsLoaded, setStaticOptionsLoaded] = useState(false);

	const fetchTemplateDataOnce = useCallback(async () => {
		if (isTemplate && templateId && !rfqData) {
			const fetchedTemplateData = await getTemplateById(templateId);
			if (fetchedTemplateData) {
				const templateData = {
					...fetchedTemplateData.data,
					id: fetchedTemplateData.id,
					name: fetchedTemplateData.name,
					companyId: fetchedTemplateData.companyId,
				};
				setRFQData(templateData);

				const formValues = formValuesChecker(templateData, true);
				formikRef?.current?.setValues(formValues);
			}
		}
		setLoadRFQData(false);
	}, [isTemplate, templateId, rfqData]);

	// Fetch static options ONCE on mount
	useEffect(() => {
		let isMounted = true;
		setStaticOptionsLoaded(false);
		Promise.all([getRFQType(), getDocumentType(), getUnitMeasures()]).then(
			([types, docs, units]) => {
				if (!isMounted) return;
				setRFQTypes(
					types?.map((type) => ({ label: type.name, value: type.code })) || []
				);
				setDocumentCategories(
					docs?.map((doc) => ({ label: doc.name, value: doc.id })) || []
				);
				setUnitMeasures(
					units?.map((unit) => ({ label: unit.name, value: unit.id })) || []
				);
				setStaticOptionsLoaded(true);
			}
		);
		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (isTemplate) {
			setLoadRFQData(true);
			fetchTemplateDataOnce();
		} else {
			// Original RFQ logic
			setRFQId(rfqId);
			try {
				if (rfqId && !rfqData) {
					setLoadRFQData(true);
					getRFQId(rfqId).then((res) => {
						if (res) {
							setRFQData(res);
							const formValues = formValuesChecker(res);
							formikRef?.current?.setValues(formValues);
						} else {
							setIsNotFound(true);
						}
						setLoadRFQData(false);
					});
				} else {
					setLoadRFQData(false);
				}
			} catch {
				setLoadRFQData(false);
				setIsNotFound(true);
				throw new Error("Failed when submit");
			}
		}
		// eslint-disable-next-line
	}, [isTemplate, isEdit, rfqId, templateId, fetchTemplateDataOnce]);

	const handleSave = useCallback(
		// additional message is outside the form
		async (additional_message, type) => {
			const values = formikRef?.current?.values;

			try {
				setValidated(false);
				setIsSubmittingForm(true);
				// calculate document_ids regardless of template status
				const document_ids =
					values?.documents
						?.filter((doc) => !doc.isRemove) // Only include non-removed documents
						.map((doc) => doc.id)
						.filter(Boolean) || [];

				const baseSubmissionFormat = {
					...values,
					additional_message,
					location: values?.address?.value || "",
					location_latitude: values?.address?.latitude || "",
					location_longitude: values?.address?.longitude || "",
					state: values?.state || "",
					submission_deadline: values?.deadline_date
						? moment(
								`${moment(values?.deadline_date).format("YYYY-MM-DD")} ${values?.deadline_time}`,
								"YYYY-MM-DD HH:mm:ss"
							).format("YYYY-MM-DD HH:mm:ss")
						: "",
					// Explicitly remove fields managed by conditional logic below
					quote: undefined,
					quote_file: undefined,
					quote_file_path: undefined,
					sections: undefined,
					returnable_schedule_file: undefined,
					returnable_schedule_file_path: undefined,
					returnable_schedule_file_id: undefined,
					...(isTemplate && {
						template_id: templateId,
						template_name:
							values?.template_name || values?.name || "New Template",
						document_ids: document_ids,
					}),
				};

				// Conditionally add quote-related fields
				let quoteSubmission = {};
				if (values?.quote_type === "file") {
					quoteSubmission = {
						quote: [], // always empty for file type
						...(isTemplate
							? {
									quote_file_id: values.quote_file_id || null,
									quote_file: null,
									quote_file_path: "",
								}
							: {
									quote_file_id: null,
									quote_file: values.quote_file,
									quote_file_path: values.quote_file_path,
								}),
					};
				} else {
					quoteSubmission = {
						quote: values?.quote || [],
						quote_file: "",
						quote_file_path: "",
						quote_file_id: null,
					};
				}

				let scheduleSubmission = {};
				if (values?.returnable_schedule_type === "file") {
					scheduleSubmission = {
						sections: [], // always empty for file type
						...(isTemplate
							? {
									returnable_schedule_file_id:
										values.returnable_schedule_file_id || null,
									returnable_schedule_file: null,
									returnable_schedule_file_path: "",
								}
							: {
									returnable_schedule_file: values.returnable_schedule_file,
									returnable_schedule_file_id: null,
									returnable_schedule_file_path:
										values.returnable_schedule_file_path,
								}),
					};
				} else {
					scheduleSubmission = {
						sections: values?.sections || [],
						returnable_schedule_file: "",
						returnable_schedule_file_path: "",
						returnable_schedule_file_id: null,
					};
				}

				const submissionFormat = {
					...baseSubmissionFormat,
					...quoteSubmission,
					...scheduleSubmission,
					quote_type: values.quote_type,
					returnable_schedule_type: values.returnable_schedule_type,
				};

				// Clean up explicitly undefined fields
				Object.keys(submissionFormat).forEach((key) => {
					if (submissionFormat[key] === undefined) {
						delete submissionFormat[key];
					}
				});

				if (isTemplate) {
					const res = await saveRFXTemplate(
						submissionFormat,
						selectedTab,
						lastStep && openModal
					);

					if (res) {
						const currentTemplateId = templateId || res.id;
						if (!templateId && res.id) {
							setTemplateId(res.id);
						}

						const fetchedData = await getTemplateById(currentTemplateId);

						if (fetchedData) {
							const templateData = {
								...fetchedData.data,
								id: fetchedData.id,
								name: fetchedData.name,
								companyId: fetchedData.companyId,
								returnableScheduleFileId: fetchedData.returnableScheduleFileId,
							};
							setRFQData(templateData);
							const formValues = formValuesChecker(templateData, isTemplate);
							formikRef?.current?.setValues(formValues);
							setValidated(true);
							setValidHandler(type);
						} else {
							setValidated(false);
						}
					} else {
						formikRef?.current?.submitForm();
						lastStep && openModal && setTouchedTabs([1, 2, 3, 4]);
						setOpenModal(false);
						setValidated(false);
					}
					setIsSubmittingForm(false);
				} else {
					const res = await saveDraftRFQ(
						rfqId,
						submissionFormat,
						selectedTab,
						lastStep && openModal
					);

					if (res) {
						setRFQData(res);
						const formValues = formValuesChecker(res, isTemplate);
						formikRef?.current?.setValues(formValues);
						setValidated(true);
						setValidHandler(type);
					} else {
						formikRef?.current?.submitForm();
						lastStep && openModal && setTouchedTabs([1, 2, 3, 4, 5]);
						setOpenModal(false);
						setValidated(false);
					}
					setIsSubmittingForm(false);
				}
			} catch (e) {
				console.error("Error during handleSave:", e);
				setIsSubmittingForm(false);
				setValidated(false);
			}
		},
		[
			lastStep,
			openModal,
			rfqId,
			selectedTab,
			isTemplate,
			templateId,
			setTemplateId,
		]
	);

	const handleSubmitOutside = async (type, additional_message) => {
		setValidHandler(null);
		tabRef?.current?.scrollIntoView();
		if (type === "laststep") {
			if (isTemplate) {
				checkIsCurrentStepValid(handleSave, additional_message, "submit");
			} else {
				setOpenModal(true);
			}
		} else {
			checkIsCurrentStepValid(handleSave, additional_message, type);
		}
	};

	useEffect(() => {
		if (validated && validHandler) {
			switch (validHandler) {
				case "exit":
					navigate("..");
					setValidated(false);
					setValidHandler(null);
					break;
				case "next":
					setSelectedTab((prev) => prev + 1);
					setValidated(false);
					setValidHandler(null);
					break;
				case "submit":
					setValidated(false);
					setValidHandler(null);
					if (isTemplate) {
						navigate(`/account/${company_id}/request-for-quote`);
					} else {
						navigate("..");
					}
					break;
				default:
					break;
			}
		}
	}, [validated, validHandler, isTemplate, templateId, company_id, dispatch]);

	useEffect(() => {
		formikRef?.current?.validateForm();
		setTouchedTabs((prev) => {
			if (prev.includes(selectedTab)) {
				return prev;
			} else {
				return [...prev, selectedTab];
			}
		});
	}, [selectedTab]);

	useEffect(() => {
		if (rfqData) {
			if (rfqData?.status && rfqData?.status.toUpperCase() === "EVALUATION") {
				navigate(`/account/${company_id}/request-for-quote`);
				return;
			}
			if (!isTemplate && !rfqData?.supplierList?.suppliers.length) {
				setDisableLastStep(true);
			} else {
				setDisableLastStep(false);
			}
			const formValues = formValuesChecker(rfqData, isTemplate);
			// only update form values if we're not in template mode or if we're editing a template
			if (!isTemplate || (isTemplate && isEdit)) {
				formikRef?.current?.setValues(formValues);
			}
			setCompletedSteps(
				isTemplate
					? [
							formValues?.template_name !== "" ? 1 : null,
							formValues?.documents.length > 0 ? 2 : null,
							formValues?.sections.filter((val) => val?.name !== "").length > 0
								? 3
								: formValues?.returnable_schedule_file
									? 3
									: null,
							formValues?.quote_type !== "file" &&
							formValues?.quote.filter((val) => val?.id !== null).length > 0
								? 4
								: formValues?.quote_file
									? 4
									: null,
						].filter((value) => value !== null)
					: [
							formValues?.type !== "" &&
							formValues?.address.value !== "" &&
							formValues?.deadline_date !== "" &&
							formValues?.deadline_time !== ""
								? 1
								: null,
							formValues?.documents.length > 0 ? 2 : null,
							formValues?.sections.filter((val) => val?.name !== "").length > 0
								? 3
								: formValues?.returnable_schedule_file
									? 3
									: null,
							formValues?.quote_type !== "file" &&
							formValues?.quote.filter((val) => val?.id !== null).length > 0
								? 4
								: formValues?.quote_file
									? 4
									: null,
							formValues?.guest.length > 0 || formValues?.suppliers.length > 0
								? 5
								: null,
						].filter((value) => value !== null)
			);
		}
	}, [rfqData, company_id, isTemplate, isEdit]);

	const handleTabChangeWithSave = async (tab) => {
		if (!isFileUploading) {
			setIsTabChanging(true);
			console.log("[debug] Tab change allowed, moving to tab", tab.value);
			// Save draft before changing tab
			await handleSave(undefined, "tabchange");
			setSelectedTab(tab.value);
			setTimeout(() => {
				setIsTabChanging(false);
			}, 100);
		}
	};

	const checkIsCurrentStepValid = async (handleOnStepValid, value, type) => {
		const currentForm = formikRef?.current;

		// Force immediate UI update
		await new Promise((resolve) => {
			setIsValidating(true);
			setTimeout(resolve, 0);
		});

		try {
			// Add small delay to ensure spinner appears --> Returnable schedules could be very long, treated specially
			if (selectedTab === 3) {
				await new Promise((resolve) => setTimeout(resolve, 100));
			}

			const errors = await currentForm?.validateForm();

			if (selectedTab === 3) {
				await new Promise((resolve) => {
					touchFieldsRecursively(
						currentForm.values.sections,
						"sections",
						currentForm
					);
					resolve();
				});
			} else {
				Object.keys(errors).forEach((fieldName) => {
					currentForm.setFieldTouched(fieldName, true);
				});
			}

			const updatedErrors = await currentForm.validateForm();

			const validationResult = validStepChecker(
				selectedTab,
				updatedErrors,
				isTemplate
			);

			const isCurrentStepValid =
				validationResult.isValid && touchedTabs.includes(selectedTab);

			if (isCurrentStepValid) {
				await handleOnStepValid(value, type);
			} else {
				showToast(validationResult.errorMessage, "Error", false);
			}
		} catch (error) {
			console.error("Error during validation:", error);
			showToast("An error occurred during validation", "Error", false);
		} finally {
			setIsValidating(false);
			setIsSubmittingForm(false);
		}
	};

	// Helper function to recursively touch all fields in a nested structure
	const touchFieldsRecursively = (values, basePath, formik) => {
		if (!values) return;

		if (Array.isArray(values)) {
			// Handle array values
			values.forEach((item, index) => {
				touchFieldsRecursively(item, `${basePath}[${index}]`, formik);
			});
		} else if (typeof values === "object" && values !== null) {
			// Handle object values
			Object.keys(values).forEach((key) => {
				const path = basePath ? `${basePath}.${key}` : key;

				// Special handling for corporate document fields
				if (key === "fields" && values.rfq_question_type_id === 10) {
					formik.setFieldTouched(path, true, true);

					// If fields has allow_document_categories, touch that specifically
					if (values[key] && values[key].allow_document_categories) {
						formik.setFieldTouched(
							`${path}.allow_document_categories`,
							true,
							true
						);
					}
				} else {
					formik.setFieldTouched(path, true);
				}

				touchFieldsRecursively(values[key], path, formik);
			});
		}
	};

	// Add ContinueButton component near other component definitions
	const ContinueButton = useCallback(
		({ onClick, disabled, lastStep }) => (
			<Button
				disabled={disabled}
				btntype={"primary"}
				type="button"
				onClick={onClick}
			>
				{isSubmittingForm || isValidating ? (
					<Spinner />
				) : lastStep ? (
					isTemplate ? (
						"Save Template"
					) : (
						"Send RFx"
					)
				) : (
					"Continue"
				)}
			</Button>
		),
		[isSubmittingForm, isValidating, isTemplate]
	);

	const { values: formikValues } = formikRef.current || {}; // Get formik values safely

	// Calculate additional disabled condition for the last step of the template
	const isTemplateQuoteFormInvalid =
		isTemplate &&
		lastStep &&
		formikValues?.quote_type === "form" &&
		isQuoteFormInvalid(formikValues?.quote);

	return (
		<>
			{isNotFound && <NotFound />}
			{!isNotFound && (
				<div className="relative overflow-auto pt-4">
					<div ref={tabRef}>
						<TabHeader
							icon={
								<BackButton
									isNew={!isEdit}
									company_id={company_id}
									isEdit={isEdit}
									isTemplate={isTemplate}
								/>
							}
							heading={
								isTemplate
									? isEdit
										? `Edit ${rfqData?.name || "RFx Template"}`
										: "Create RFx Template"
									: isEdit
										? `Edit ${rfqData?.name || "RFx"}`
										: "CREATE YOUR RFx"
							}
							isLoading={loadRFQData && isEdit}
						>
							<Flex
								gap={3}
								align={"center"}
							>
								{!isTemplate && (
									<TextButton
										disabled={isSubmittingForm || isFileUploading}
										onClick={() => handleSubmitOutside("save")}
										data-testid="save-button"
									>
										Save Draft
									</TextButton>
								)}

								{!isTemplate && (
									<Button
										btntype={"secondary"}
										type="button"
										disabled={isSubmittingForm}
										onClick={onOpen}
										data-testid="preview-button"
									>
										<Flex
											gap={2}
											align={"center"}
										>
											<Icon icon={"view"} />
											Preview
										</Flex>
									</Button>
								)}

								<ContinueButton
									onClick={() =>
										handleSubmitOutside(lastStep ? "laststep" : "next")
									}
									disabled={
										lastStep
											? !isValidForm ||
												disableLastStep ||
												isSubmittingForm ||
												isTemplateQuoteFormInvalid ||
												isFileUploading
											: isSubmittingForm || isFileUploading
									}
									lastStep={lastStep}
								/>
							</Flex>
						</TabHeader>
					</div>
					<div
						id="create-rfq-content"
						className="p-6"
					>
						<RFQTabNavigation
							isLoading={loadRFQData}
							tabButtonList={tabButtonList}
							selectedTab={selectedTab}
							completedSteps={completedSteps}
							touchedTabs={touchedTabs}
							formikRef={formikRef}
							isTemplate={isTemplate}
							isEdit={isEdit}
							rfqData={rfqData}
							onChangeTab={handleTabChangeWithSave}
						/>

						{!loadRFQData && staticOptionsLoaded && (isTemplate || rfqData) ? (
							<Formik
								onSubmit={() => {}}
								initialValues={
									isTemplate
										? rfqData
											? formValuesChecker(rfqData, isTemplate)
											: formValuesChecker(null, isTemplate)
										: formValuesChecker(rfqData, isTemplate)
								}
								innerRef={formikRef}
								validationSchema={validationSchema(isTemplate)}
								validateOnChange={enableOnChangeFormik}
							>
								{({
									isSubmitting,
									isValid,
									errors,
									values,
									setFieldError,
									setFieldValue,
								}) => {
									if (isValid !== isValidForm) {
										setIsValidForm(isValid);
									}
									return (
										<Form>
											{selectedTab === 1 &&
												(isTemplate ? (
													<TemplateDetail
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														data={null}
														isEdit={isEdit}
													/>
												) : (
													<ProjectDetail
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														rfqData={rfqData}
														rfqTypes={rfqTypes}
													/>
												))}
											{selectedTab === 2 &&
												(isTabChanging ? (
													<div className="flex h-[50vh] items-center justify-center">
														<Spinner size="xl" />
													</div>
												) : (
													<Documentation
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														rfqData={rfqData}
														documentCategories={documentCategories}
														selectedTab={selectedTab}
														isTemplate={isTemplate}
													/>
												))}
											{selectedTab === 3 &&
												(isTabChanging ? (
													<div className="flex h-[50vh] items-center justify-center">
														<Spinner size="xl" />
													</div>
												) : (
													<ReturnableSchedules
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														rfqData={rfqData}
														setFieldError={setFieldError}
														setFieldValue={setFieldValue}
														isTemplate={isTemplate}
													/>
												))}
											{selectedTab === 4 &&
												(isTabChanging ? (
													<div className="flex h-[50vh] items-center justify-center">
														<Spinner size="xl" />
													</div>
												) : (
													<Quote
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														unitMeasures={unitMeasures}
														setFieldValue={setFieldValue}
														isTemplate={isTemplate}
													/>
												))}
											{lastStep && !isTemplate && (
												<Suppliers
													rfqId={rfqId}
													showErrorMessage={false}
												/>
											)}
											<Flex
												gap={4}
												align={"center"}
												className="float-end py-10"
											>
												{!isTemplate && (
													<TextButton
														disabled={isSubmittingForm || isFileUploading}
														onClick={() => handleSubmitOutside("save")}
													>
														Save Draft
													</TextButton>
												)}
												{selectedTab > 1 && (
													<Button
														disabled={isSubmittingForm || isFileUploading}
														btntype={"base"}
														type="button"
														onClick={() => {
															setSelectedTab((prev) => prev - 1);
															tabRef?.current?.scrollIntoView();
														}}
													>
														Back
													</Button>
												)}
												<ContinueButton
													onClick={() =>
														handleSubmitOutside(lastStep ? "laststep" : "next")
													}
													disabled={
														lastStep
															? !isValid ||
																disableLastStep ||
																isSubmittingForm ||
																isTemplateQuoteFormInvalid ||
																isFileUploading
															: isSubmittingForm || isFileUploading
													}
													lastStep={lastStep}
												/>
											</Flex>
											{!isTemplate && isOpen && (
												<PreviewRFQ
													isOpen={isOpen}
													onClose={onClose}
													rfqData={rfqData}
													unitMeasures={unitMeasures}
													documentCategories={documentCategories}
													rfqTypes={rfqTypes}
												/>
											)}
											{!isTemplate && (
												<SubmitRFQModal
													isOpen={openModal}
													setIsOpen={setOpenModal}
													rfqId={rfqId}
													rfqDetail={rfqData}
													isSaving={isSubmittingForm}
													companyName={company?.name}
													isSubmitting={isSubmitting}
													handleSubmit={handleSubmitOutside}
												/>
											)}
										</Form>
									);
								}}
							</Formik>
						) : (
							<div className="min-h-[70vh]">
								<LoadingSpinner />
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default CreateRFQPage;
